import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { FullPageSpinner } from '../../components/FullPageSpinner.js';
import { resolveClinicPaymentGate } from './jwt-claims.js';
import { useSession } from './SessionProvider.js';

/**
 * Guard de rotas operacionais da clínica. Implementa o gate de
 * pagamento (Modelo C híbrido — ADR 0009).
 *
 * Verificação 100% client-side via JWT claim
 * `current_clinic_subscription_status` (injetado pelo
 * `custom_access_token_hook` — migration 0063). A autorização real
 * acontece sempre no banco via RLS — este guard é só UX (não navegar
 * o user pra rotas que ele não vai conseguir acessar).
 *
 * Decisões:
 *   - **superadmin** → libera (acesso cross-clinic pra suporte)
 *   - **sem clinic** → libera (não chega nas rotas operacionais)
 *   - **`'incomplete'`** → redireciona pra `/clinic/payment-pending`
 *     (gate ativo: primeira fatura não confirmada)
 *   - **`'canceled'` / `'paused'`** → redireciona pra `/clinic/suspended`
 *   - **`'trialing'` / `'active'` / `'past_due'`** → libera
 *
 * **Deve estar montado dentro de `<RequireAuth>`** — se não tiver
 * sessão, RequireAuth já redirecionou pra /login antes desse guard
 * rodar.
 *
 * Uso:
 * ```tsx
 * <Route element={<RequireAuth />}>
 *   <Route element={<RequireClinicSubscriptionActive />}>
 *     <Route path="/dashboard" element={<DashboardPage />} />
 *   </Route>
 * </Route>
 * ```
 */
export function RequireClinicSubscriptionActive() {
  const { loading, session } = useSession();
  const location = useLocation();

  if (loading) return <FullPageSpinner />;

  // Defensivo: sem sessão, RequireAuth deveria ter pegado antes.
  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  const decision = resolveClinicPaymentGate(session.access_token);

  if (decision === 'payment_pending') {
    // Não passa state.from — após pagamento confirmado, owner deve
    // recarregar a sessão e RequireClinicSubscriptionActive vai
    // permitir entrada normal nas rotas. Encaminhar pra `from` aqui
    // criaria loop (rota destino ainda bloqueada até refresh do JWT).
    return <Navigate to="/clinic/payment-pending" replace />;
  }
  if (decision === 'suspended') {
    return <Navigate to="/clinic/suspended" replace />;
  }
  return <Outlet />;
}
